import { Namespace, type useT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { roleEnumToKey, type RoleKey } from "~/shared/api/user/user.grpc";

/** A role the select offers: the id is a wire-backed key, never a loose string. */
export interface RoleOption {
  id: RoleKey;
  name: string;
}

/** The roles an admin may assign from the create / edit user dialogs. */
export const getRoleOptions = (t: ReturnType<typeof useT>): RoleOption[] => [
  { id: "USER_ROLE_RECEIVER_USER", name: t(Namespace.Employees, "position.receiver_user") },
  { id: "USER_ROLE_PROVIDER_DISPATCHER", name: t(Namespace.Employees, "position.dispatcher") },
  { id: "USER_ROLE_DRIVER", name: t(Namespace.Employees, "position.driver") },
  { id: "USER_ROLE_PROVIDER_MECHANIC", name: t(Namespace.Employees, "position.mechanic") },
  { id: "USER_ROLE_PROVIDER_LOADER", name: t(Namespace.Employees, "position.loader") },
  { id: "USER_ROLE_SUPPORT", name: t(Namespace.Employees, "position.support") },
  { id: "USER_ROLE_PROVIDER_OWNER", name: t(Namespace.Employees, "position.management") },
  {
    id: "USER_ROLE_DISPOSAL_SITE_OWNER",
    name: t(Namespace.Employees, "position.disposal_site_owner"),
  },
  {
    id: "USER_ROLE_GOVERNMENT_AUDITOR",
    name: t(Namespace.Employees, "position.government_auditor"),
  },
  {
    id: "USER_ROLE_GOVERNMENT_AUDITOR_EDITOR",
    name: t(Namespace.Employees, "position.government_auditor_editor"),
  },
  { id: "USER_ROLE_GOVERNMENT_ECO", name: t(Namespace.Employees, "position.government_eco") },
  { id: "USER_ROLE_GPS_INNER_SERVICE", name: t(Namespace.Employees, "position.gps_inner_service") },
  {
    id: "USER_ROLE_PROVIDER_OWNER_VIEW_ONLY",
    name: t(Namespace.Employees, "position.management_view"),
  },
  { id: "USER_ROLE_PROVIDER_SALES", name: t(Namespace.Employees, "position.provider_sales") },
];

/** The subset a provider may staff its own org with, on the Employees page. */
const STAFF_ROLE_IDS: RoleKey[] = [
  "USER_ROLE_PROVIDER_DISPATCHER",
  "USER_ROLE_DRIVER",
  "USER_ROLE_PROVIDER_MECHANIC",
  "USER_ROLE_PROVIDER_LOADER",
  "USER_ROLE_SUPPORT",
  "USER_ROLE_PROVIDER_OWNER",
];

export const getStaffRoleOptions = (t: ReturnType<typeof useT>): RoleOption[] =>
  getRoleOptions(t).filter((o) => STAFF_ROLE_IDS.includes(o.id));

/** The role's label, or "" for an unset or unknown role. */
export const roleLabel = (t: ReturnType<typeof useT>, role?: ConnectProto.UserRole): string => {
  if (!role) return "";
  const key = roleEnumToKey(role);
  return key ? t(Namespace.Enum, key) : "";
};
